<template>
  <RsModal
    :value="value"
    :title="title"
    size="xl"
    dialog-class="rs-modal-checkboxes"
    :button-text="primaryButtonText"
    :is-centered="isCentered"
    @input="$emit('input', false)"
    @submit="handleSubmit"
  >
    <slot name="topContent" />
    <div class="row mx-n4 px-4 pb-3 bg-white border-bottom z-index-2 position-relative align-items-end">
      <RsFormInput
        v-model="searchValue"
        icon="search"
        class="input-search mb-0 border-right-0"
        type="text"
        :label="searchLabel"
        :debounce="200"
      />
      <RsButton
        v-if="(searchValue || '').trim().length"
        variant="cancel"
        class="btn-clear-search ml-2"
        @click="handleClearSearch"
      >
        <i class="fas fa-times" />
      </RsButton>

      <span v-if="showNumSelected" class="selected-count font-weight-bold ml-2 mb-2">[{{ selectedOptions.length }} {{ $ts('userManagement.modals.checkboxes.selected') }}]</span>

      <div class="flex-fill" />

      <div v-if="!maxSelect">
        <RsButton
          v-if="showSelectAllButtons"
          variant="secondary"
          hollow
          class="btn-select-all"
          @click="handleSelectAll"
        >
          {{ $ts('actions.selectAll') }}
        </RsButton>
        <RsButton
          v-if="showSelectAllButtons"
          variant="secondary"
          hollow
          class="ml-2 btn-deselect-all"
          @click="handleDeselectAll"
        >
          {{ $ts('actions.deselectAll') }}
        </RsButton>
      </div>
    </div>
    <div :class="`${checkboxGroupClasses} modal-content-height mx-n4 my-n3 pt-3`">
      <div class="h-100 overflow-auto px-2 py-3">
        <div class="row mx-0">
          <div
            v-for="(option, index) in filteredOptions"
            :key="index"
            class="col-6 col-lg-4"
          >
            <RsFormCheckbox
              :id="`rs_modal_checkboxes_${index}`"
              :label="option.text"
              :model-value="isValueSelected(option)"
              class="mb-0"
              :disabled="isMaxSelectedReached(option.value)"
              @change="handleValueChecked($event, option)"
            />
            <sup
              v-if="markedOptions.includes(option.value)"
              class="option-indicator"
            >*</sup>
          </div>
        </div>
      </div>
    </div>
  </RsModal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';
import RsButton from './RsButton.vue';
import RsFormCheckbox from './RsFormCheckbox.vue';

export default defineComponent({
  props: {
    /**
     * Used to toggle v-model view state of RsModal
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * Checkbox options to select
     */
    options: {
      type: Array as PropType<ISelectOption[]>,
      default: () => [],
    },
    /**
     * Checkbox options selected
     */
    values: {
      type: Array as PropType<ISelectOption['value'][]>,
      default: () => [],
    },
    /**
     * Modal title
     */
    title: {
      type: String,
      default: 'Select Options',
    },

    /**
     * Max allowed selection
     */
    maxSelect: {
      type: Number,
      default: undefined,
    },

    isCentered: {
      type: Boolean,
      default: false,
    },
    searchLabel: {
      type: String,
      default: 'Search',
    },

    showNumSelected: {
      type: Boolean,
      default: false,
    },

    showSelectAllButtons: {
      type: Boolean,
      default: true,
    },

    checkboxGroupClasses: {
      type: String,
      default: '',
    },

    primaryButtonText: {
      type: String,
      default: 'Apply Filters',
    },
    /**
     * Determines if selected ISelectOption objects are emitted upon confirm.
     * If set to false, selected ISelectOption values are emitted.
     */
    isEmitSelectedOptionObjects: {
      type: Boolean,
      default: false,
    },
    /**
     * An array of values that are marked with asterisk
     */
    markedOptions: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
  },

  data() {
    return {
      selectedOptions: [] as ISelectOption['value'][],
      selectedOptionObjects: [] as ISelectOption[],
      searchValue: '' as string,
    };
  },

  computed: {
    /**
     * Applies the search box filter against options' text property
     */
    filteredOptions(): ISelectOption[] {
      return this.options.filter((o) => o.text.toLowerCase().includes(this.searchValue.toLowerCase()));
    },
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.value) {
          this.selectedOptions = this.values;
        }
      },
    },
  },

  methods: {
    /**
     * Determines via boolean if option matches a selected value
     * @param option `ISelectOption`
     */
    isValueSelected(option: ISelectOption) {
      return this.selectedOptions.includes(option.value);
    },

    /**
     * Determines if passed option value is to be added or removed from selected options based on state param from the checkbox event.
     * @param state `boolean`
     * @param option `ISelectOption`
     */
    handleValueChecked(state: boolean, option: ISelectOption) {
      this.selectedOptions = state ? [
        ...this.selectedOptions,
        option.value,
      ] : this.selectedOptions.filter((val) => val !== option.value);
    },

    /**
     * Maps `options` `value` property to `this.selectedOptions` to select all.
     */
    handleSelectAll() {
      this.selectedOptions = this.options.map((option) => option.value);
    },

    /**
     * Resets `this.selectedOptions` to an empty array
     */
    handleDeselectAll() {
      this.selectedOptions = [];
    },

    /**
     * Clears search box value
     */
    handleClearSearch() {
      this.searchValue = '';
    },

    /**
     * Emits the current state of `this.selectedOptions`
     */
    handleSubmit() {
      if (this.isEmitSelectedOptionObjects) {
        this.selectedOptionObjects = this.options.filter((option) => this.selectedOptions.includes(option.value));
        this.$emit('on-submit', this.selectedOptionObjects);
      } else {
        this.$emit('on-submit', this.selectedOptions);
      }
    },

    /**
     * Checks against a `maxSelect` prop for determining if we need to disable checkboxes after
     * a certain number of items are selected.
     * @param value
     */
    isMaxSelectedReached(value: string) {
      const max = Number(this.maxSelect);

      if (max) {
        return this.selectedOptions.length >= max && !this.selectedOptions.includes(value);
      }

      return false;
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.rs-modal-checkboxes {
  .modal-content-height {
    height: 69vh;
    overflow-y: hidden;
    overflow-x: hidden;
  }

  .input-search {
    width: 400px;
  }

.selected-count {
  color: $secondary-color-dark;
  font-size: 16px;
}
.option-indicator {
  color: $error;
  font-size: 14px;
  margin-left: -2px;
}
}

</style>
